// ABOUTME: Log entries of one job attempt, optionally filtered by level: loads every page, then
// ABOUTME: tails new entries (afterSeq polling every 2 s) while the attempt is still live.

import { useCallback, useEffect, useRef, useState } from 'react';

import { getAttemptLogs } from '../../api/jobApi';
import { JobLogEntry, LogLevel } from '../../types';

const TAIL_INTERVAL_MS = 2000;
const PAGE_SIZE = 1000;

export function useAttemptLogs(
    jobId: string | null | undefined,
    attempt: number | null | undefined,
    level: LogLevel[] | undefined,
    live: boolean,
) {
    const [entries, setEntries] = useState<JobLogEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const lastSeq = useRef(0);
    // The generation whose read is running, so two reads of the same log never interleave.
    const inFlight = useRef<number | null>(null);
    // Bumped whenever the attempt or the level filter changes: answers of an older generation are
    // dropped instead of being appended to the wrong list.
    const generation = useRef(0);
    // Set when the attempt or the filter changes: the first page read afterwards replaces the
    // entries instead of being appended, so the old lines stay on screen until the new ones arrive.
    const replaceNext = useRef(false);
    const levelKey = (level ?? []).slice().sort().join(',');

    /** Appends every entry after lastSeq, following the pages until the end of the log. */
    const fetchAfter = useCallback(async () => {
        const gen = generation.current;
        if (!jobId || !attempt || inFlight.current === gen) return;
        inFlight.current = gen;
        const levels = levelKey ? (levelKey.split(',') as LogLevel[]) : undefined;
        try {
            let hasMore = true;
            while (hasMore) {
                // Sequential on purpose: each page starts after the last seq of the previous one.
                // eslint-disable-next-line no-await-in-loop
                const page = await getAttemptLogs(jobId, attempt, {
                    level: levels,
                    afterSeq: lastSeq.current,
                    limit: PAGE_SIZE,
                });
                if (gen !== generation.current) return;
                lastSeq.current = Math.max(lastSeq.current, page.lastSeq);
                if (replaceNext.current) {
                    replaceNext.current = false;
                    setEntries(page.entries);
                } else if (page.entries.length) {
                    setEntries(prev => [...prev, ...page.entries]);
                }
                hasMore = page.hasMore && page.entries.length > 0;
            }
            setError(null);
        } catch (e) {
            if (gen === generation.current) setError(e instanceof Error ? e.message : String(e));
        } finally {
            if (inFlight.current === gen) inFlight.current = null;
        }
    }, [jobId, attempt, levelKey]);

    useEffect(() => {
        generation.current += 1;
        lastSeq.current = 0;
        setError(null);
        if (!jobId || !attempt) {
            setEntries([]);
            return;
        }
        replaceNext.current = true;
        setLoading(true);
        const gen = generation.current;
        fetchAfter().finally(() => {
            if (gen === generation.current) setLoading(false);
        });
    }, [fetchAfter]);

    useEffect(() => {
        if (!live) {
            // The attempt just ended (or never was live): one last read picks up its final lines.
            fetchAfter();
            return undefined;
        }
        const interval = setInterval(() => {
            if (!document.hidden) fetchAfter();
        }, TAIL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [live, fetchAfter]);

    return { entries, loading, error };
}
